<template>
    <div>
      <HeaderComponent />
  
      <main class="container mx-auto px-4 py-8">
        <div class="flex flex-col md:flex-row items-center space-x-0 md:space-x-4 mb-4">
          <!-- Country Selection -->
          <div class="flex-1 w-1/3 md:w-auto">
            <label for="country" class="block text-sm font-medium text-gray-700">Country</label>
            <select
              id="country"
              v-model="selectedCountry" 
              @change="updateCitiesAndStates"
              class="mt-1 block w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="">-- Select Country --</option>
              <option v-for="(label, code) in countryMap" :key="code" :value="code">
                {{ code }}
              </option>
            </select>
          </div>
  
          <!-- City Selection -->
          <div class="flex-1 w-1/3 md:w-auto">
            <label for="city" class="block text-sm font-medium text-gray-700">{{ cityDropDownLabel }}</label>
            <select
              id="city"
              @change="onCityChange"
              v-model="selectedCity"
              class="mt-1 block w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="">-- Select {{ cityDropDownLabel }} --</option>
              <option v-for="city in filteredCities" :key="city" :value="city">
                {{ city }}
              </option>
            </select>
          </div>
  
          <!-- Search Button -->
          <button
            @click="performSearch"
            class="w-1/3 md:w-auto bg-blue-500 text-white font-bold p-2 mt-6 rounded hover:bg-blue-700 transition duration-300 ease-in-out flex items-center justify-center"
          >
          <img src="@/assets/search.png" alt="search" width="18px;" height="auto">
          </button>
        </div>
  
        <!-- Loading States -->
        <div v-if="loading" class="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div v-for="i in 6" :key="i" class="bg-gray-200 animate-pulse rounded-lg overflow-hidden">
            <div class="h-32 bg-gray-300"></div>
            <div class="p-4 h-60">
              <div class="h-6 bg-gray-300 rounded mb-2"></div>
              <div class="h-4 w-3/4 bg-gray-300 rounded mb-4"></div>
              <div class="h-4 w-1/4 bg-gray-300 rounded mb-4"></div>
              <div class="h-4 w-1/4 bg-gray-300 rounded mb-4"></div>
              <div class="h-4 w-1/4 bg-gray-300 rounded mb-4"></div>
              <div class="h-4 w-1/3 bg-gray-300 rounded mb-2"></div>
            </div>
          </div>
        </div>
  
        <!-- Error State -->
        <div v-else-if="error" class="text-center py-8 text-red-600">
          <p>{{ errorMessage }}</p>
        </div>
  
        <!-- No Data State -->
        <div v-else-if="!data">
          <p class="text-center py-8">No data found.</p>

          <div class="max-w-4xl mx-auto">
            <!-- Header Section -->
            <div class="text-center mb-12 pt-10">
              <h1 class="text-2xl md:text-2xl font-bold mb-4 text-gray-900">
                Salesforce Expert Guide
              </h1>
              <p class="text-lg text-gray-600 max-w-2xl mx-auto">
                Follow below steps to search salesforce expert.
              </p>
            </div>
            <!-- sfdc-expert Guide-->
            <div class="space-y-6 md:space-y-8">
              <ol>
                  <li>
                      <span class="step-title">Step 1:</span> After selecting <strong>"Find Saleforce Expert"</strong> under Salesforce Tools, you'll be taken to the  <strong>"sfdc-expert"</strong> page. Here, you can select a <strong>"Country"</strong> and <strong>"City"</strong> from the dropdown menus, then click the <strong>"Search"</strong> icon to view the results.
                      <img src="@/assets/search-sfdc-experts.png" alt="search sfdc-expert">
                  </li>
              </ol>
            </div>
          </div>

        </div>
  
        <!-- Data Display -->
        <div v-else>
          <h1 class="text-3xl font-bold mb-6">{{ data.search_term }} in {{ data.search_location }}</h1>
          <h2 class="text-2xl font-semibold mb-4">{{ data.title }}</h2>
          <p class="mb-4">{{ data.meta_description }}</p>
  
          <!-- Company Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div v-for="company in data.listOfCompany" :key="company.companyName" class="border rounded-lg p-4 shadow-md">
              <img :src="getFullImageUrl(company.fileUrl)" :alt="company.companyName" class="w-full h-32 object-contain mb-4">
              <h3 class="text-xl font-semibold mb-2">{{ company.companyName }}</h3>
              <p class="text-sm mb-2">{{ company.tagLine }}</p>
              <p class="text-sm mb-2">Rating: {{ company.star }} / 5</p>
              <p class="text-sm mb-2">{{ company.review }} Review(s)</p>
              <p class="text-sm mb-2">Projects: {{ company.projectCount }}</p>
              <p class="text-sm">Certified Experts: {{ company.certifiedExpert }}</p>
              <div class="flex justify-end">
                <button 
                  @click="openModal(company)" 
                  class="bg-gray-500 text-white font-bold py-1 px-2 text-sm rounded hover:bg-gray-600 transition duration-300 ease-in-out"
                >
                  Show Company Info
                </button>
              </div>
            </div>
          </div>
  
          <!-- FAQ Section -->
          <div class="mt-8">
            <h3 class="text-xl font-semibold mb-4">FAQ</h3>
            <div itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
              <h4 itemprop="name" class="font-semibold">{{ data.faq1 }}</h4>
              <div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">
                <div itemprop="text">
                  <p v-html="data.faq1ans"></p>
                </div>
              </div>
            </div>
            <br/>
            <div itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
              <h4 itemprop="name" class="font-semibold">{{ data.faq2 }}</h4>
              <div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">
                <div itemprop="text">
                  <p v-html="data.faq2ans"></p>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        <!-- Modal -->
        <div v-if="isModalOpen" class="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center">
          <div class="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full">
            <div class="flex justify-end">
              <button 
                @click="closeModal" 
                class="bg-gray-400 text-white font-bold py-1 px-2 text-sm rounded hover:bg-gray-600 transition duration-300 ease-in-out flex items-center"
              >
                <i class="fas fa-times"></i> 
              </button>
            </div>
            <div class="flex flex-col items-center" v-if="selectedCompany">
              <img :src="getFullImageUrl(selectedCompany.fileUrl)" :alt="selectedCompany.companyName" class="w-32 h-32 object-contain mb-4">
              <h3 class="text-xl font-semibold mb-2">{{ selectedCompany.companyName }}</h3>
              <p class="text-sm mb-2">{{ selectedCompany.tagLine }}</p>
              <p class="text-sm mb-2">{{ selectedCompany.review }} Review(s)</p>
              <div class="flex items-center mb-2">
                <span v-for="n in Math.floor(selectedCompany.star)" :key="n" class="text-yellow-500">
                  <i class="fas fa-star"></i>
                </span>
                <span v-if="isHalfStar(selectedCompany.star)" class="text-yellow-500">
                  <i class="fas fa-star-half-alt"></i>
                </span>
                <span v-for="n in 5 - Math.ceil(selectedCompany.star)" :key="'empty-' + n" class="text-gray-300">
                  <i class="far fa-star"></i>
                </span>
              </div>
              <p class="text-sm mb-2">Certified Experts: {{ selectedCompany.certifiedExpert }}</p>
              <p class="text-sm mb-2">Project Completed: {{ selectedCompany.projectCount }}</p>
              <p class="text-sm mb-2">Serving in {{ selectedCompany.locations }}</p>
              <p class="text-sm mb-2">Head Office: {{ selectedCompany.headOffice }}</p>
              <p class="text-sm">{{ selectedCompany.shortDescription }}</p>
              <div class="flex space-x-4 mt-4">
                <a :href="selectedCompany.url_website" target="_blank" class="text-gray-500 hover:text-gray-900" aria-label="External Link">
                  <i class="fas fa-external-link-alt h-6 w-6"></i>
                </a>
                <a :href="selectedCompany.url_facebook" target="_blank" class="text-gray-500 hover:text-gray-900" aria-label="Facebook">
                  <i class="fab fa-facebook-f h-6 w-6"></i>
                </a>
                <a :href="selectedCompany.url_tweeter" target="_blank" class="text-gray-500 hover:text-gray-900" aria-label="Twitter">
                  <i class="fab fa-twitter h-6 w-6"></i>
                </a>
                <a :href="selectedCompany.url_linkedin" target="_blank" class="text-gray-500 hover:text-gray-900" aria-label="LinkedIn">
                  <i class="fab fa-linkedin-in h-6 w-6"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>
  
      <FooterComponent />
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted , watch, computed} from 'vue';
  import HeaderComponent from "../components/Header.vue";
  import FooterComponent from "../components/Footer.vue";
  import { useRoute, useRouter } from 'vue-router'
  import {useCommonHead} from '../composables/useCommonHead';
  
  // State
  const data = ref(null)
  const loading = ref(false)
  const error = ref(false)
  const errorMessage = ref('')
  const isModalOpen = ref(false)
  const selectedCompany = ref(null)
  const selectedCountry = ref('')
  const selectedCountryCode = ref('')
  const selectedCity = ref('')
  const selectedCityCode = ref('')
  const filteredCities = ref([])
  const cityDropDownLabel = ref('City')
  
  const route = useRoute()
  const router = useRouter()
  //const category = ref(route.params.category); // Use ref to make it reactive
  
  // Computed
  const pageTitle = computed(() => `${data.value?.post_description || ''} - Arrify`)
  const pageDescription = computed(() => data.value?.meta_description || '')
  const currentUrl = computed(() => ``);
  useCommonHead(pageTitle.value, pageDescription.value, currentUrl.value);

  watch([pageTitle, pageDescription, currentUrl], () => {
    useCommonHead(pageTitle.value, pageDescription.value, currentUrl.value);
  });


    // Constants
    const countryMap = {
    "Germany": "de",
    "Australia": "au",
    "United Kingdom": "gb",
    "USA Cities": "us",
    "USA States": "us-s"
  }
  
  const germanyCityLabelMap = {
    "Berlin": "berlin",
    "Frankfurt": "frankfurt"
  }

  const australiaCityLabelMap = {
      "Sydney": "sydney",
      "Melbourne": "melbourne",
      "Brisbane": "brisbane",
      "Perth": "perth",
      "Adelaide": "adelaide",
      "Gold Coast": "gold-coast",
      "Cranbourne": "cranbourne",
      "Canberra": "canberra",
      "Central Coast": "central-coast",
      "Wollongong": "wollongong",
      "Hobart": "hobart",
      "Geelong": "geelong",
      "Townsville": "townsville",
      "Ipswich": "ipswich",
      "Newcastle": "newcastle",
      "Cairns": "cairns",
      "Darwin": "darwin",
      "Toowoomba": "toowoomba",
      "Launceston": "launceston"
    }

    const ukCityLabelMap = {
      "Belfast": "belfast",
      "Bradford": "bradford",
      "Nottingham": "nottingham",
      "Bolton": "bolton",
      "Sunderland": "sunderland",
      "Southampton": "southampton",
      "Plymouth": "plymouth",
      "Kingston upon Hull": "kingston-upon-hull",
      "Derby": "derby",
      "Westminster": "westminster",
      "Wolverhampton": "wolverhampton",
      "Stoke on Trent": "stoke-on-trent",
      "Portsmouth": "portsmouth",
      "Abertawe": "abertawe",
      "Birmingham": "birmingham",
      "Newcastle": "newcastle",
      "Ipswich": "ipswich",
      "London": "london",
      "Leeds": "leeds",
      "Solihull": "solihull",
      "Aberdeen": "aberdeen",
      "Norwich": "norwich",
      "Peterborough": "peterborough",
      "Bournemouth": "bournemouth",
      "Basildon": "basildon",
      "York": "york",
      "Warrington": "warrington",
      "Huddersfield": "huddersfield",
      "Reading": "reading",
      "Southend": "southend",
      "Barnsley": "barnsley",
      "Swindon": "swindon",
      "Luton": "luton",
      "Northampton": "northampton",
      "Milton Keynes": "milton-keynes",
      "Oxford": "oxford",
      "Glasgow": "glasgow",
      "Bristol": "bristol",
      "Manchester": "manchester",
      "Sheffield": "sheffield",
      "Liverpool": "liverpool",
      "Edinburgh": "edinburgh",
      "Coventry": "coventry",
      "Caerdydd": "caerdydd",
      "Leicester": "leicester"
    }

    const usCityLabelMap = {
      "Cleveland": "cleveland",
      "Pittsburgh": "pittsburgh",
      "Austin": "austin",
      "Cincinnati": "cincinnati",
      "Kansas City": "kansas-city",
      "Manhattan": "manhattan",
      "Indianapolis": "indianapolis",
      "Columbus": "columbus",
      "Charlotte": "charlotte",
      "Virginia Beach": "virginia-beach",
      "Bronx": "bronx",
      "Milwaukee": "milwaukee",
      "Providence": "providence",
      "Jacksonville": "jacksonville",
      "Salt Lake City": "salt-lake-city",
      "Nashville": "nashville",
      "Richmond": "richmond",
      "Memphis": "memphis",
      "Raleigh": "raleigh",
      "New Orleans": "new-orleans",
      "Louisville": "louisville",
      "Oklahoma City": "oklahoma-city",
      "Bridgeport": "bridgeport",
      "Buffalo": "buffalo",
      "Fort Worth": "fort-worth",
      "Hartford": "hartford",
      "Tucson": "tucson",
      "Omaha": "omaha",
      "El Paso": "el-paso",
      "Honolulu": "honolulu",
      "McAllen": "mcallen",
      "Hempstead": "hempstead",
      "Albuquerque": "albuquerque",
      "Birmingham": "birmingham",
      "Sarasota": "sarasota",
      "Dayton": "dayton"
    }

    const usStatesLabelMap = {
      "Iowa": "iowa",
      "Idaho": "idaho",
      "Illinois": "illinois",
      "Indiana": "indiana",
      "Kansas": "kansas",
      "Kentucky": "kentucky",
      "Louisiana": "louisiana",
      "Massachusetts": "massachusetts",
      "Maryland": "maryland",
      "Maine": "maine",
      "Michigan": "michigan",
      "Minnesota": "minnesota",
      "Missouri": "missouri",
      "Mississippi": "mississippi",
      "South Carolina": "south-carolina",
      "South Dakota": "south-dakota",
      "Tennessee": "tennessee",
      "Texas": "texas",
      "Utah": "utah",
      "Virginia": "virginia",
      "Vermont": "vermont",
      "Wisconsin": "wisconsin",
      "West Virginia": "west-virginia",
      "Wyoming": "wyoming",
      "New York": "new-york",
      "Washington": "washington",
      "Montana": "montana",
      "North Carolina": "north-carolina",
      "North Dakota": "north-dakota",
      "Nebraska": "nebraska",
      "New Hampshire": "new-hampshire",
      "New Jersey": "new-jersey",
      "New Mexico": "new-mexico",
      "Nevada": "nevada",
      "Ohio": "ohio",
      "Oklahoma": "oklahoma",
      "Oregon": "oregon",
      "Pennsylvania": "pennsylvania",
      "Puerto Rico": "puerto-rico",
      "Rhode Island": "rhode-island",
      "Alaska": "alaska",
      "Alabama": "alabama",
      "Arkansas": "arkansas",
      "Arizona": "arizona",
      "California": "california",
      "Colorado": "colorado",
      "Connecticut": "connecticut",
      "District of Columbia": "district-of-columbia",
      "Delaware": "delaware",
      "Florida": "florida",
      "Georgia": "georgia",
      "Hawaii": "hawaii"
    }

    const cityLabelMaps = {
      de: germanyCityLabelMap,
      au: australiaCityLabelMap,
      gb: ukCityLabelMap,
      us: usCityLabelMap,
      'us-s': usStatesLabelMap
    }

    const countryCityMap = {
      de: ["Berlin", "Frankfurt"],
      au: [
          "Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide", "Gold Coast", "Cranbourne",
          "Canberra", "Central Coast", "Wollongong", "Hobart", "Geelong", "Townsville",
          "Ipswich", "Newcastle", "Cairns", "Darwin", "Toowoomba", "Launceston"
      ],
      gb: [
          "Belfast", "Bradford", "Nottingham", "Bolton", "Sunderland", "Southampton", "Plymouth",
          "Kingston upon Hull", "Derby", "Westminster", "Wolverhampton", "Stoke on Trent",
          "Portsmouth", "Abertawe", "Birmingham", "Newcastle", "Ipswich", "London", "Leeds",
          "Solihull", "Aberdeen", "Norwich", "Peterborough", "Bournemouth", "Basildon", "York",
          "Warrington", "Huddersfield", "Reading", "Southend", "Barnsley", "Swindon", "Luton",
          "Northampton", "Milton Keynes", "Oxford", "Glasgow", "Bristol", "Manchester",
          "Sheffield", "Liverpool", "Edinburgh", "Coventry", "Caerdydd", "Leicester"
      ],
      us: [
          "Cleveland", "Pittsburgh", "Austin", "Cincinnati", "Kansas City", "Manhattan",
          "Indianapolis", "Columbus", "Charlotte", "Virginia Beach", "Bronx", "Milwaukee",
          "Providence", "Jacksonville", "Salt Lake City", "Nashville", "Richmond", "Memphis",
          "Raleigh", "New Orleans", "Louisville", "Oklahoma City", "Bridgeport", "Buffalo",
          "Fort Worth", "Hartford", "Tucson", "Omaha", "El Paso", "Honolulu", "McAllen",
          "Hempstead", "Albuquerque", "Birmingham", "Sarasota", "Dayton"
      ],
      "us-s": [
          "Iowa", "Idaho", "Illinois", "Indiana", "Kansas", "Kentucky", "Louisiana", "Massachusetts",
          "Maryland", "Maine", "Michigan", "Minnesota", "Missouri", "Mississippi", "South Carolina",
          "South Dakota", "Tennessee", "Texas", "Utah", "Virginia", "Vermont", "Wisconsin",
          "West Virginia", "Wyoming", "New York", "Washington", "Montana", "North Carolina",
          "North Dakota", "Nebraska", "New Hampshire", "New Jersey", "New Mexico", "Nevada",
          "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Puerto Rico", "Rhode Island", "Alaska",
          "Alabama", "Arkansas", "Arizona", "California", "Colorado", "Connecticut", "District of Columbia",
          "Delaware", "Florida", "Georgia", "Hawaii"
      ]
    }
  
  // Methods
  const updateCitiesAndStates = () => {
    selectedCountryCode.value = countryMap[selectedCountry.value];
    console.log('selected Country : ' + selectedCountry.value);
    console.log('selected CountryCode  : ' + selectedCountryCode.value);

    if(selectedCountryCode.value == 'us-s'){
    cityDropDownLabel.value = 'State';
    }
    if(selectedCountryCode.value == 'us'){
    cityDropDownLabel.value = 'City';
    }
    filteredCities.value = countryCityMap[selectedCountryCode.value] || [];
    selectedCity.value = ''
  }
  
  const onCityChange = () => {
    console.log('selected City : ' + selectedCity.value);
    const cityMap = cityLabelMaps[selectedCountryCode.value]
    if (cityMap) {
      selectedCityCode.value = cityMap[selectedCity.value]
    }
    console.log('selected City Code : ' + selectedCityCode.value);
  }
  
  const performSearch = () => {
  const country = selectedCountryCode.value
  const location = selectedCityCode.value
  updateUrlParam(country, location)
  fetchData(country, location)
}

const updateUrlParam = (newCountry, newCity) => {
  router.push({ path: `/connect/sfdc-expert/${newCountry}/${newCity}` })
}
  
  const openModal = (company) => {
    selectedCompany.value = company
    isModalOpen.value = true
  }
  
  const closeModal = () => {
    isModalOpen.value = false
    selectedCompany.value = null
  }
  
  const isHalfStar = (rating) => rating % 1 !== 0
  
  const getFullImageUrl = (fileUrl) => `https://cdn.arrify.com/wp-content/uploads/${fileUrl}`
  
  const fetchData = async (country, location) => {
    loading.value = true
    error.value = false
    try {
      const response = await fetch(
        `https://cms.arrify.com/wp-json/afconnect/v1/search?p1=sfdc-expert&p2=${country}&p3=${location}`
      )
      const result = await response.json()
      
      if (result.error) {
        error.value = true
        errorMessage.value = result.message
      } else {
        data.value = result.data
      }
    } catch (err) {
      console.error('Error fetching data:', err)
      error.value = true
      errorMessage.value = 'An unexpected error occurred. Please try again later.'
    } finally {
      loading.value = false
    }
  }

  onMounted(() => {

   const params = route.params.params;
   console.log('params : ' + params);

   if (params && params.length) {
    const params_values = route.params.params.join(',').split(',');
   
    selectedCountryCode.value = params_values[0];
    selectedCityCode.value = params_values[1];
    selectedCountry.value = Object.keys(countryMap).find(key => countryMap[key] === params_values[0])
    
    if(selectedCountryCode.value == 'us-s'){
        cityDropDownLabel.value = 'State';
    }
    if(selectedCountryCode.value == 'us'){
        cityDropDownLabel.value = 'City';
    }
    filteredCities.value = countryCityMap[selectedCountryCode.value] || []
    
    const cityMap = cityLabelMaps[selectedCountryCode.value]
    if (cityMap) {
      selectedCity.value = Object.keys(cityMap).find(key => cityMap[key] === params_values[1])
    }
    console.log('selected Country : ' + selectedCountry.value);
    console.log('selected CountryCode  : ' + selectedCountryCode.value);
    console.log('selected CityCode  : ' + selectedCityCode.value);
    console.log('selected City  : ' + selectedCity.value);
    
    fetchData(selectedCountryCode.value, selectedCityCode.value)
  }
})
</script>

<style scoped>
/* Optional styles for loading animation */
.animate-pulse {
  animation: pulse 1.5s infinite;
}
@keyframes pulse {
  0%, 100% {
    background-color: #e0e0e0;
  }
  50% {
    background-color: #f0f0f0;
  }
}

li {
    margin-bottom: 8px;
}
ul {
    margin-left: 20px;
}
.step-title {
    font-weight: bold;
    color: #0073e6;
}
.note {
    font-style: italic;
    color: #555;
}
</style>